﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, Briefcase } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { recruitmentService } from '@/services/recruitment.service';

export function RecruitmentWidget() {
  const { has } = usePermission();

  const statsQ = useQuery({
    queryKey: ['dashboard', 'recruitment', 'stats'],
    queryFn: () => recruitmentService.stats(),
    enabled: has(PERMISSIONS.RECRUITMENT_VIEW),
  });

  const candidatesQ = useQuery({
    queryKey: ['dashboard', 'recruitment', 'candidates'],
    queryFn: () => recruitmentService.listCandidates({ page: 1, limit: 5 }),
    enabled: has(PERMISSIONS.RECRUITMENT_VIEW),
  });

  if (!has(PERMISSIONS.RECRUITMENT_VIEW)) return null;

  const stats = statsQ.data;
  const candidates = candidatesQ.data?.data ?? [];

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Briefcase />}
        title="Recruitment Pipeline"
        action={
          <Link to="/hrms/recruitment" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        {statsQ.isLoading ? (
          <div className="h-10 animate-pulse rounded-lg bg-muted/50" />
        ) : stats ? (
          <div className="flex flex-wrap gap-2">
            {stats.pipeline.total != null && (
              <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Total: {stats.pipeline.total}
              </div>
            )}
            {stats.pipeline.in_pipeline != null && (
              <div className="rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
                Active: {stats.pipeline.in_pipeline}
              </div>
            )}
            {stats.pipeline.converted != null && (
              <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                Hired: {stats.pipeline.converted}
              </div>
            )}
            {stats.pipeline.rejected != null && (
              <div className="rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                Rejected: {stats.pipeline.rejected}
              </div>
            )}
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Candidates
          </p>
          {candidatesQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          ) : candidatesQ.isError ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load</p>
              <Button size="sm" variant="outline" onClick={() => candidatesQ.refetch()}>Retry</Button>
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <Briefcase className="h-7 w-7 opacity-30" />
              <p className="text-sm text-muted-foreground">No candidates yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <span className="truncate font-medium">{c.first_name} {c.last_name}</span>
                  {c.stage && (
                    <Badge variant="outline" className="ml-2 shrink-0">
                      {c.stage}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
